import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'danger-ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  className = '',
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-monday-bg disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses: Record<string, string> = {
    primary:
      'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 active:from-primary-700 active:to-primary-800 focus:ring-primary-200 shadow-primary hover:shadow-primary-lg',
    secondary:
      'bg-white/90 text-monday-text border border-monday-border hover:bg-monday-header active:bg-neutral-100 focus:ring-primary-200 shadow-xs',
    ghost:
      'bg-transparent text-monday-text-muted hover:bg-monday-header hover:text-monday-text active:bg-neutral-100 focus:ring-primary-200',
    destructive:
      'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 focus:ring-error-200 shadow-error',
    'danger-ghost':
      'bg-transparent text-monday-text-muted hover:bg-error-50 hover:text-error-600 active:bg-error-100 focus:ring-error-200',
  };

  const sizeClasses: Record<string, string> = {
    sm: 'px-3.5 py-1.5 text-xs min-h-[32px]',
    md: 'px-[1.125rem] py-2 text-sm min-h-[38px]',
    lg: 'px-6 py-2.5 text-base min-h-[46px]',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;
